import * as React from 'react';
import * as NavigationMenu from '@radix-ui/react-navigation-menu';
import { styled } from 'styled-components';
import { Icon, tk } from '@sunwebgroup/ui-components';
import { useIntl } from '@/intl';

import { HeaderLink } from './HeaderLink';
import { SunwebLogo, SunwebLogoCompact } from './Logo';
import { Menu, MenuTrigger, NestedSubMenu, SubMenu } from './Menu';
import { Navbar } from './Navbar';

import type { NavigationProps } from './types';
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks';
import { createHeaderClickEvent } from '@/domain/header/analytics';
import { useLockBodyScroll } from '@/scopes/common/components/hooks/useLockBodyScroll';
import type { MenuItem } from '@/cms/fields/types/common';

export function DesktopNavigation({ data, extraData }: NavigationProps) {
  const intl = useIntl();
  const { sendEvent } = useAnalytics();

  const [openSubmenuId, setOpenSubmenuId] = React.useState('');
  useLockBodyScroll(Boolean(openSubmenuId));

  const handleMenuItemClick = (result: string[], children: MenuItem[] | undefined) => {
    sendEvent(
      createHeaderClickEvent({
        result,
        hasChildren: children && children.length > 0,
      })
    );
  };

  return (
    <>
      <Navbar value={openSubmenuId} onValueChange={setOpenSubmenuId}>
        <Navbar.List>
          <Navbar.Item>
            <HeaderLink href="/" data-qa="header-logo-link">
              <HeaderLogo>
                <SunwebLogo />
                <SunwebLogoCompact />
              </HeaderLogo>
              <ScreenReaderOnlyText>{intl.formatMessage({ id: 'link_home' })}</ScreenReaderOnlyText>
            </HeaderLink>
          </Navbar.Item>
          {data && (
            <Navbar.Item>
              <MenuTrigger
                onClick={() => handleMenuItemClick([data.text], data.children)}
                onPointerMove={(e: React.PointerEvent) => e.preventDefault()}
                onPointerLeave={(e: React.PointerEvent) => e.preventDefault()}
              >
                {data.text}
                <Icon glyph="IconChevronDownSmall" size="md" />
              </MenuTrigger>
              <Menu>
                {data.children?.map((menuItem, menuItemKey) => (
                  <Menu.Item
                    onClick={() => handleMenuItemClick([data.text, menuItem.text], menuItem.children)}
                    key={menuItemKey}
                  >
                    <SubMenu label={menuItem.text}>
                      {menuItem.children?.map((subMenuItem, subMenuItemKey) => (
                        <SubMenu.Item
                          onClick={() =>
                            handleMenuItemClick([data.text, menuItem.text, subMenuItem.text], subMenuItem.children)
                          }
                          key={subMenuItemKey}
                        >
                          {subMenuItem.children && subMenuItem.children.length > 0 ? (
                            <NestedSubMenu label={subMenuItem.text}>
                              {subMenuItem.children.map((nestedSubMenuItem, nestedSubMenuItemKey) => (
                                <HeaderLink
                                  onClick={() =>
                                    handleMenuItemClick(
                                      [data.text, menuItem.text, subMenuItem.text, nestedSubMenuItem.text],
                                      nestedSubMenuItem.children
                                    )
                                  }
                                  key={nestedSubMenuItemKey}
                                  href={nestedSubMenuItem.href}
                                >
                                  {nestedSubMenuItem.text}
                                </HeaderLink>
                              ))}
                            </NestedSubMenu>
                          ) : (
                            <HeaderLink
                              onClick={() =>
                                handleMenuItemClick([data.text, menuItem.text, subMenuItem.text], subMenuItem.children)
                              }
                              href={subMenuItem.href}
                            >
                              {subMenuItem.text}
                            </HeaderLink>
                          )}
                        </SubMenu.Item>
                      ))}
                    </SubMenu>
                  </Menu.Item>
                ))}
              </Menu>
            </Navbar.Item>
          )}
          {extraData?.map((menuItem, menuItemKey) => (
            <Navbar.Item key={menuItemKey}>
              <HeaderLink onClick={() => handleMenuItemClick([menuItem.text], undefined)} href={menuItem.href}>
                {menuItem.text}
              </HeaderLink>
            </Navbar.Item>
          ))}
        </Navbar.List>

        {/* Places the active submenu on the bottom of the header */}
        <ActiveContent>
          <NavigationMenuViewport forceMount />
        </ActiveContent>
      </Navbar>
      {openSubmenuId && <Overlay />}
    </>
  );
}

const NavigationMenuViewport = styled(NavigationMenu.Viewport)`
  &[data-state='closed'] {
    display: none;
  }
`;

const ScreenReaderOnlyText = styled.span`
  clip: rect(0 0 0 0);
  height: 1px;
  overflow: hidden;
  position: absolute;
  white-space: nowrap;
  width: 1px;
`;

const ActiveContent = styled.div`
  background: ${tk('color_semantic_bg')};
  border-top: 1px solid ${tk('color_semantic_border')};
  left: 0;
  margin: 0;
  position: absolute;
  right: 0;
  top: 100%;
  z-index: 1;
`;

const Overlay = styled.div`
  background: ${tk('color_palette_transparent_black_50')};
  height: calc(100vh - 100%);
  left: 0;
  position: absolute;
  top: 101%; // 1% to cover the border of the header
  width: 100vw;
`;

const HeaderLogo = styled.div`
  ${SunwebLogo} {
    display: none;
  }
  ${SunwebLogoCompact} {
    display: inline;
  }
  @media ${tk('mq_tablet')} {
    ${SunwebLogoCompact} {
      display: none;
    }
    ${SunwebLogo} {
      display: block;
    }
  }
`;
